import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiErrorMessage } from "@/api/client";
import { Upload, Search, Loader2, CheckCircle2, XCircle, X, FlaskConical, RotateCcw } from "lucide-react";

const DIAG_LABEL = { pendiente: "Pendiente", en_progreso: "En progreso", completado: "Completado" };

export default function AdminParticipants() {
  const [participants, setParticipants] = useState(null);
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const [importOpen, setImportOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/participants");
      setParticipants(data.participants);
    } catch (err) {
      setError(apiErrorMessage(err));
    }
  };

  useEffect(() => { load(); }, []);

  const realParticipants = (participants || []).filter((p) => !p.is_demo);
  const demoParticipants = (participants || []).filter((p) => p.is_demo);

  const filtered = realParticipants.filter((p) =>
    !search || p.full_name.toLowerCase().includes(search.toLowerCase()) || p.rut_display.includes(search) || p.unit.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-black text-slate-800">Matrícula</h1>
          <p className="text-sm text-slate-500">{participants ? `${realParticipants.length} participantes registrados` : "Cargando..."}</p>
        </div>
        <button data-testid="open-import-modal-btn" onClick={() => setImportOpen(true)} className="flex items-center gap-2 px-5 py-2.5 bg-brand-slate hover:bg-slate-900 text-white text-xs font-bold rounded-xl">
          <Upload className="w-4 h-4" /> Importar matrícula
        </button>
      </div>

      <div className="relative max-w-sm">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input data-testid="participants-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por nombre, RUT o unidad..." className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-slate-300 bg-white text-sm" />
      </div>

      {error && <div className="text-rose-600 text-sm font-semibold">{error}</div>}
      {!participants ? <Loader2 className="w-6 h-6 animate-spin text-brand-accent" /> : (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-x-auto">
          <table className="w-full text-xs" data-testid="participants-table">
            <thead>
              <tr className="text-left text-slate-400 uppercase text-[10px] border-b bg-slate-50">
                <th className="py-3 px-4">Nombre</th><th>RUT</th><th>Correo</th><th>Unidad</th><th>Diagnóstico</th><th>Entregadas</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.participant_id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-2.5 px-4 font-bold text-slate-700"><Link to={`/admin/participantes/${p.participant_id}`} className="hover:text-brand-accent">{p.full_name}</Link></td>
                  <td className="text-slate-500">{p.rut_display}</td>
                  <td className="text-slate-500">{p.email}</td>
                  <td className="text-slate-500">{p.unit}</td>
                  <td><span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-md ${p.diagnostic_status === "completado" ? "bg-emerald-100 text-emerald-700" : p.diagnostic_status === "en_progreso" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}>{DIAG_LABEL[p.diagnostic_status]}</span></td>
                  <td className="font-bold text-slate-700">{p.delivered_count} / {p.total_weeks}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && <p className="text-center text-xs text-slate-400 py-8">Sin resultados.</p>}
        </div>
      )}

      <DemoSection demoParticipants={demoParticipants} onReset={load} />

      {importOpen && <ImportModal onClose={() => setImportOpen(false)} onDone={() => { setImportOpen(false); load(); }} />}
    </div>
  );
}

function DemoSection({ demoParticipants, onReset }) {
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState("");

  if (demoParticipants.length === 0) return null;

  const handleReset = async () => {
    if (!window.confirm("¿Reiniciar la cuenta DEMO? Se borrarán sus respuestas de diagnóstico, resultados y actividades para dejarla lista para una nueva prueba.")) return;
    setResetting(true);
    setError("");
    try {
      await api.post("/admin/demo/reset");
      onReset();
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="bg-amber-50 border-2 border-dashed border-amber-300 rounded-2xl p-5" data-testid="demo-section">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2">
          <FlaskConical className="w-4 h-4 text-amber-700" />
          <h2 className="text-xs font-black uppercase tracking-wider text-amber-800" data-testid="demo-section-title">Usuario Demo — No contabiliza</h2>
        </div>
        <button data-testid="demo-reset-btn" onClick={handleReset} disabled={resetting} className="flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold rounded-xl disabled:opacity-60 transition-colors">
          {resetting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />} Reiniciar DEMO
        </button>
      </div>
      <p className="text-[11px] text-amber-700 mb-3">Esta cuenta se usa solo para pruebas. Sus respuestas y resultados quedan siempre excluidos de estadísticas, promedios, IAC, informe de brechas y exportaciones oficiales.</p>
      {error && <div className="text-xs font-semibold text-rose-700 bg-rose-100 border border-rose-200 rounded-lg px-3 py-2 mb-3">{error}</div>}
      <table className="w-full text-xs" data-testid="demo-participants-table">
        <thead>
          <tr className="text-left text-amber-700/70 uppercase text-[10px] border-b border-amber-200">
            <th className="py-2">Nombre</th><th>RUT</th><th>Correo</th><th>Diagnóstico</th><th>Entregadas</th>
          </tr>
        </thead>
        <tbody>
          {demoParticipants.map((p) => (
            <tr key={p.participant_id} className="border-b border-amber-200/60">
              <td className="py-2 font-bold text-amber-900">{p.full_name}</td>
              <td className="text-amber-700">{p.rut_display}</td>
              <td className="text-amber-700">{p.email}</td>
              <td><span className="text-[10px] font-bold uppercase px-2 py-1 rounded-md bg-amber-100 text-amber-700">{DIAG_LABEL[p.diagnostic_status]}</span></td>
              <td className="font-bold text-amber-800">{p.delivered_count} / {p.total_weeks}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ImportModal({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [pastedText, setPastedText] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const runPreview = async () => {
    setLoading(true); setError(""); setPreview(null);
    try {
      const form = new FormData();
      if (file) form.append("file", file);
      if (pastedText.trim()) form.append("pasted_text", pastedText);
      const { data } = await api.post("/admin/participants/import/preview", form, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const confirmImport = async () => {
    setConfirming(true); setError("");
    try {
      const { data } = await api.post("/admin/participants/import/confirm", { batch_id: preview.batch_id });
      setResult(data);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto p-6" data-testid="import-modal">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-black text-slate-800">Importar matrícula</h2>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-400" /></button>
        </div>

        {result ? (
          <div className="text-center py-8">
            <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
            <p className="font-bold text-slate-800">{result.inserted} participantes importados correctamente.</p>
            {result.skipped_duplicates.length > 0 && <p className="text-xs text-amber-600 mt-2">Se omitieron {result.skipped_duplicates.length} filas duplicadas.</p>}
            <button onClick={onDone} className="mt-5 px-6 py-2.5 bg-brand-slate text-white text-xs font-bold rounded-xl">Cerrar</button>
          </div>
        ) : !preview ? (
          <div className="space-y-4">
            <p className="text-xs text-slate-500">Sube un archivo CSV/XLSX con columnas RUT | Nombre completo | Correo institucional | Unidad (también soporta RUN+DV y Apellidos separados), o pega una tabla.</p>
            <input data-testid="import-file-input" type="file" accept=".csv,.xlsx,.xls" onChange={(e) => setFile(e.target.files[0])} className="text-xs" />
            <div className="text-center text-xs text-slate-400">— o —</div>
            <textarea data-testid="import-paste-textarea" rows={5} placeholder="Pega aquí una tabla (con encabezado)" value={pastedText} onChange={(e) => setPastedText(e.target.value)} className="w-full border border-slate-300 rounded-xl p-3 text-xs font-mono" />
            {error && <div className="text-xs font-semibold text-rose-600 bg-rose-50 border border-rose-200 rounded-xl px-4 py-3">{error}</div>}
            <button data-testid="import-preview-btn" disabled={loading || (!file && !pastedText.trim())} onClick={runPreview} className="px-6 py-2.5 bg-brand-slate text-white text-xs font-bold rounded-xl disabled:opacity-50 flex items-center gap-2">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />} Ver vista previa
            </button>
          </div>
        ) : (
          <div className="space-y-4" data-testid="import-preview-results">
            <div className="flex gap-4 text-xs font-bold">
              <span className="text-slate-600">Total: {preview.total_rows}</span>
              <span className="text-emerald-600">Válidas: {preview.valid_rows}</span>
              <span className="text-rose-600">Con errores: {preview.invalid_rows}</span>
            </div>
            <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-xl">
              <table className="w-full text-[11px]">
                <thead><tr className="bg-slate-50 text-left text-slate-400 uppercase text-[9px]"><th className="p-2">Fila</th><th>Estado</th><th>Nombre</th><th>RUT</th><th>Correo</th><th>Errores</th></tr></thead>
                <tbody>
                  {preview.rows.map((r) => (
                    <tr key={r.row_number} className={`border-t ${r.valid ? "" : "bg-rose-50"}`}>
                      <td className="p-2">{r.row_number}</td>
                      <td>{r.valid ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> : <XCircle className="w-3.5 h-3.5 text-rose-500" />}</td>
                      <td>{r.full_name}</td>
                      <td>{r.rut_display || "-"}</td>
                      <td>{r.email}</td>
                      <td className="text-rose-600">{r.errors.join(" · ")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {error && <div className="text-xs font-semibold text-rose-600 bg-rose-50 border border-rose-200 rounded-xl px-4 py-3">{error}</div>}
            <div className="flex justify-end gap-3">
              <button onClick={() => setPreview(null)} className="px-5 py-2.5 border border-slate-300 text-xs font-bold rounded-xl text-slate-600">Volver</button>
              <button data-testid="import-confirm-btn" disabled={confirming || preview.valid_rows === 0} onClick={confirmImport} className="px-6 py-2.5 bg-emerald-600 text-white text-xs font-bold rounded-xl disabled:opacity-50 flex items-center gap-2">
                {confirming && <Loader2 className="w-4 h-4 animate-spin" />} Confirmar importación ({preview.valid_rows})
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
